/**
 * The complaint record (piece 34, Complaints; decision 1468): who raised it,
 * who it is about, what they said in their own words, who investigated, what
 * was found, what was done and when it closed. 🔒 Its audience is HR, Admin
 * and the complainant and never the management chain, decided by
 * `canViewHrRecord` and nowhere else.
 */
import { AUDIT_ACTIONS } from '../audit/retention';

export const COMPLAINT_STATUSES = ['Open', 'UnderInvestigation', 'Closed'] as const;
export type ComplaintStatus = (typeof COMPLAINT_STATUSES)[number];

/** The complaints page's own address, and the routes the browser asks for, relative to the API root. */
export const HR_COMPLAINTS_ROUTE = '/hr/complaints';
export const HR_COMPLAINTS_PATH = '/hr/complaints';
export const HR_COMPLAINT_ROUTE_CHECK_PATH = '/hr/complaints/route-check';
export function complaintActionPath(id: string, action: 'take' | 'findings' | 'close'): string {
  return `${HR_COMPLAINTS_PATH}/${encodeURIComponent(id)}/${action}`;
}

/** The longest summary kept: a complaint in the complainant's own words, not a file. */
export const COMPLAINT_SUMMARY_MAXIMUM = 4000;

export function complaintSummaryProblem(summary: unknown): string | null {
  if (typeof summary !== 'string' || summary.trim() === '') return 'A complaint says what happened, in your own words.';
  if (summary.length > COMPLAINT_SUMMARY_MAXIMUM) return `A complaint is at most ${COMPLAINT_SUMMARY_MAXIMUM} characters; attach nothing here.`;
  return null;
}

/** 🔒 Closing records what was found and what was done; neither is optional (piece 34). */
export function complaintCloseProblem(input: { findings?: unknown; actionTaken?: unknown }): string | null {
  if (typeof input.findings !== 'string' || input.findings.trim() === '') return 'A complaint closes with the findings written down.';
  if (typeof input.actionTaken !== 'string' || input.actionTaken.trim() === '') return 'A complaint closes with the action taken written down, even where it is none.';
  return null;
}

/** The refusal a move off the lifecycle answers. */
export function complaintTransitionProblem(from: ComplaintStatus, to: ComplaintStatus): string | null {
  if (from === 'Open' && to === 'UnderInvestigation') return null;
  if (from === 'UnderInvestigation' && to === 'Closed') return null;
  if (from === 'Closed') return 'This complaint is closed, and a closed complaint is not reopened or edited.';
  return from === 'Open' ? 'An open complaint is taken for investigation before anything is recorded on it.' : 'This complaint is already under investigation.';
}

/**
 * 🔒 Whether a complaint about this employment goes outside instead (decision
 * 2680, amending 0137): its subject holds a role that reads complaints, HR or
 * Admin, however many hold it. Then no record opens, the route is shown, and
 * the audit row says only that the route was offered and when.
 */
export function routesOutside(aboutEmploymentId: string | null, readerEmploymentIds: readonly string[]): boolean {
  return aboutEmploymentId !== null && readerEmploymentIds.includes(aboutEmploymentId);
}

/** One complaint as its page draws it. */
export interface ComplaintRow {
  id: string;
  raisedAt: string;
  raisedByEmploymentId: string;
  raisedByName: string;
  aboutEmploymentId: string | null;
  aboutName: string | null;
  summary: string;
  status: ComplaintStatus;
  investigatingName: string | null;
  findings: string | null;
  actionTaken: string | null;
  closedAt: string | null;
  /** True for the complainant's own complaint. */
  mine: boolean;
}

/** What the complaints route answers: the complaints the viewer's audience reaches, and who a complaint may name. */
export interface ComplaintsAnswer {
  /** True for HR and Admin, who take, record and close; false for a complainant reading their own. */
  works: boolean;
  rows: ComplaintRow[];
  /** The Organization's active employments a complaint may be about, the viewer's own left out. */
  subjects: { employmentId: string; name: string }[];
  /** Whether the viewer holds an active employment at our own company, which is what raising one needs. */
  mayRaise: boolean;
}

/** What raising, or asking ahead of raising, answers: a record opened, or the outside route shown and nothing kept. */
export type RaiseComplaintAnswer =
  | { kind: 'Raised'; id: string }
  | { kind: 'RouteOffered'; route: string };

/** The audit family a complaint writes (piece 9; decision 1468), read off the one actions table. 🔨 None of them carries the allegation. */
export const COMPLAINT_AUDIT_ACTIONS = {
  raised: AUDIT_ACTIONS.complaintRaised,
  investigationTaken: AUDIT_ACTIONS.complaintInvestigationTaken,
  closed: AUDIT_ACTIONS.complaintClosed,
  routeOffered: AUDIT_ACTIONS.complaintRouteOffered,
} as const;
